import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import SignupLayout from '../Layout.jsx';
import AddressAutocomplete from '../../../components/AddressAutocomplete.jsx';
import { FACILITY_TYPES } from '../../../constants/facilityTypes.js';
import { useDraftAutosave } from '../../../hooks/useDraftAutosave.js';
import { labelClass, inputClass, selectClass, primaryBtnClass, secondaryBtnClass, STATES, saveDraftPatch } from '../shared.jsx';

const EMPTY = { name: '', facility_type: '', address: '', city: '', state: 'CA', zip: '', license_number: '' };

export default function GroupFacility({ facility: initialFacility, progress }) {
  const [facility, setFacility] = useState({ ...EMPTY, ...(initialFacility || {}) });
  const [submitting, setSubmitting] = useState(false);

  useDraftAutosave('group.facility', facility);

  const canAdvance = facility.name?.trim() && facility.facility_type && facility.address?.trim() && facility.city?.trim() && facility.state && /^\d{5}(-\d{4})?$/.test(facility.zip || '');

  const handleContinue = async () => {
    if (!canAdvance || submitting) return;
    setSubmitting(true);
    await saveDraftPatch({ group: { facility } });
    router.visit('/signup/group/training');
  };

  return (
    <SignupLayout progress={progress}>
      <div className="space-y-4">
        <h2 className="text-2xl/9 font-bold tracking-tight text-gray-900">Tell us about your facility</h2>
        <p className="text-sm text-gray-600">This helps us tailor your training experience.</p>

        <div className="space-y-3 mt-6">
          <div>
            <label className={labelClass}>Facility Name <span className="text-red-500">*</span></label>
            <input className={inputClass} value={facility.name} onChange={(e) => setFacility({ ...facility, name: e.target.value })} placeholder="Enter facility name" />
          </div>
          <div>
            <label className={labelClass}>Facility Type <span className="text-red-500">*</span></label>
            <select className={selectClass} value={facility.facility_type} onChange={(e) => setFacility({ ...facility, facility_type: e.target.value })}>
              <option value="">Select facility type</option>
              {FACILITY_TYPES.map((ft) => <option key={ft.value} value={ft.value}>{ft.abbr} — {ft.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Address <span className="text-red-500">*</span></label>
            <AddressAutocomplete
              className={inputClass}
              value={facility.address}
              onChange={(v) => setFacility({ ...facility, address: v })}
              onSelect={(s) => setFacility({ ...facility, address: s.address, city: s.city, state: s.state, zip: s.zip })}
              placeholder="Start typing your address…"
            />
          </div>
          <div className="grid grid-cols-6 gap-3">
            <div className="col-span-3">
              <label className={labelClass}>City <span className="text-red-500">*</span></label>
              <input className={inputClass} value={facility.city} onChange={(e) => setFacility({ ...facility, city: e.target.value })} placeholder="Enter city" />
            </div>
            <div className="col-span-1">
              <label className={labelClass}>State <span className="text-red-500">*</span></label>
              <select className={selectClass} value={facility.state} onChange={(e) => setFacility({ ...facility, state: e.target.value })}>
                {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className={labelClass}>ZIP Code <span className="text-red-500">*</span></label>
              <input className={inputClass} value={facility.zip} onChange={(e) => setFacility({ ...facility, zip: e.target.value })} placeholder="Enter ZIP code" />
            </div>
          </div>
          <div>
            <label className={labelClass}>Facility License Number <span className="text-gray-400">(Optional)</span></label>
            <input className={inputClass} value={facility.license_number} onChange={(e) => setFacility({ ...facility, license_number: e.target.value })} placeholder="Enter facility license number" />
          </div>
        </div>

        <div className="flex justify-between gap-3 mt-6">
          <button type="button" onClick={() => router.visit('/signup/group/buyer')} className={secondaryBtnClass}>Back</button>
          <button type="button" onClick={handleContinue} disabled={!canAdvance || submitting} className={primaryBtnClass}>
            {submitting ? 'Saving…' : 'Continue'}
          </button>
        </div>
      </div>
    </SignupLayout>
  );
}
